import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImageIcon, Plus, Pencil, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { API_URL } from '@/lib/api';

const emptyForm = {
  title: '',
  subtitle: '',
  image: '',
  link: '',
  cta: '',
  order: '0',
  active: true,
  description: '',
};

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API_URL}/banners`);
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      title: b.title || '',
      subtitle: b.subtitle || '',
      image: b.image || '',
      link: b.link || '',
      cta: b.cta || '',
      order: String(b.order ?? 0),
      active: b.active !== false,
      description: b.description || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        title: form.title,
        subtitle: form.subtitle || undefined,
        image: form.image,
        link: form.link || undefined,
        cta: form.cta || undefined,
        order: parseInt(form.order, 10) || 0,
        active: form.active,
        description: form.description || undefined,
      };
      if (editingId) {
        await axios.patch(`${API_URL}/banners/${editingId}`, body);
        toast.success('Banner updated');
      } else {
        await axios.post(`${API_URL}/banners`, body);
        toast.success('Banner created');
      }
      closeDialog();
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/banners/${deleteId}`);
      toast.success('Banner deleted');
      setDeleteId(null);
      fetchBanners();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await axios.patch(`${API_URL}/banners/${b.id}`, { active: !b.active });
      toast.success(!b.active ? 'Banner enabled' : 'Banner disabled');
      fetchBanners();
    } catch {
      toast.error('Update failed');
    }
  };

  return (
    <div data-testid="banners-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Banners</h1>
          <p className="text-slate-500 mt-1">Home / promo slides shown in the store app</p>
        </div>
        <Button onClick={openCreate} className="bg-purple-950 hover:bg-purple-900">
          <Plus className="w-4 h-4 mr-2" />
          New banner
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : banners.length === 0 ? (
          <div className="empty-state py-16">
            <ImageIcon className="w-12 h-12 mb-3 text-slate-300" />
            <p className="text-lg font-medium">No banners</p>
            <p className="text-sm text-slate-500 mt-1">Create one for the storefront carousel</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-20">Order</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Image</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {banners.map((b) => (
                <TableRow key={b.id}>
                  <TableCell className="tabular-nums">{b.order ?? 0}</TableCell>
                  <TableCell className="font-medium">{b.title || '—'}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-slate-500 text-sm">{b.image || '—'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={b.active !== false} onCheckedChange={() => toggleActive(b)} />
                      <Badge variant="outline" className={b.active !== false ? 'text-emerald-700' : 'text-slate-500'}>
                        {b.active !== false ? 'On' : 'Off'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(b)}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => setDeleteId(b.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit banner' : 'New banner'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} />
            </div>
            <div>
              <Label>Image URL *</Label>
              <Input value={form.image} onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))} required placeholder="https://..." />
            </div>
            <div>
              <Label>Link URL</Label>
              <Input value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} />
            </div>
            <div>
              <Label>CTA label</Label>
              <Input value={form.cta} onChange={(e) => setForm((f) => ({ ...f, cta: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <Switch id="active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-purple-950 hover:bg-purple-900">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Delete banner
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Banners;
